'use client';

import { SvgChevronleft, SvgChevronright } from '@common/icon';
import { cn } from '@core/utils';
import { usePathname, useSearchParams } from 'next/navigation';
import { useOptimistic, useTransition } from 'react';

import { useCustomRouter } from '@/hooks/useCustomRouter';

interface MonthPickerProps {
  month: number;
  hasPreviousMonth?: boolean;
  hasNextMonth?: boolean;
}

const buildHrefWithMonth = (
  pathname: string,
  search: URLSearchParams,
  nextMonth: number,
): string => {
  const next = new URLSearchParams(search);
  next.set('month', String(nextMonth));
  next.delete('theme');

  return `${pathname}?${next.toString()}`;
};

export const MonthPicker = ({
  month,
  hasPreviousMonth,
  hasNextMonth,
}: MonthPickerProps) => {
  const router = useCustomRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [isPending, startTransition] = useTransition();
  const [optimisticMonth, setOptimisticMonth] = useOptimistic(month);

  const prevMonth = optimisticMonth === 1 ? 12 : optimisticMonth - 1;
  const nextMonth = optimisticMonth === 12 ? 1 : optimisticMonth + 1;

  const handleNavigate = (target: number) => {
    startTransition(() => {
      setOptimisticMonth(target);
      router.replace(buildHrefWithMonth(pathname, searchParams, target), {
        scroll: false,
      });
    });
  };

  return (
    <div
      aria-busy={isPending}
      className="flex w-full items-center justify-center"
    >
      <button
        type="button"
        aria-label={`${prevMonth}월로 이동`}
        disabled={hasPreviousMonth === false}
        onClick={() => handleNavigate(prevMonth)}
        className="flex size-32 items-center justify-center rounded-xxsmall text-Gray-700 transition-[background-color,opacity] duration-150 hover:bg-Gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-B-500 active:bg-Gray-100 disabled:text-Gray-300 disabled:hover:bg-transparent disabled:active:bg-transparent"
      >
        <SvgChevronleft className="size-20" />
      </button>
      <span
        className={cn(
          'm-0 w-90 text-center font-700 text-Gray-black transition-opacity duration-150 typo-h5',
          isPending && 'opacity-60',
        )}
      >
        {`${optimisticMonth}월`}
      </span>
      <button
        type="button"
        aria-label={`${nextMonth}월로 이동`}
        disabled={hasNextMonth === false}
        onClick={() => handleNavigate(nextMonth)}
        className="flex size-32 items-center justify-center rounded-xxsmall text-Gray-700 transition-[background-color,opacity] duration-150 hover:bg-Gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-B-500 active:bg-Gray-100 disabled:text-Gray-300 disabled:hover:bg-transparent disabled:active:bg-transparent"
      >
        <SvgChevronright className="size-20" />
      </button>
    </div>
  );
};
